/**
 * Block grouping and segmentation utilities.
 * Pure functions for splitting Notion blocks into groups and segments.
 * @module utils/blocks
 */

/**
 * Recursively collects heading blocks with their level and text.
 * @param {Array<object>} blocks
 * @returns {Array<{text: string, level: number}>}
 */
export function collectHeadings(blocks) {
  const headings = [];
  function walk(bs) {
    for (const b of bs || []) {
      if (b.type === "heading_1" || b.type === "heading_2" || b.type === "heading_3") {
        const text = (b.rich_text || []).map(s => s.text).join("") || b.text || "";
        if (text.trim()) {
          const level = parseInt(b.type.slice(-1), 10);
          headings.push({ text: text.trim(), level });
        }
      }
      if (b.children) walk(b.children);
    }
  }
  walk(blocks);
  return headings;
}

/**
 * Splits blocks at each divider, creating page-break groups.
 * @param {Array<object>} blocks
 * @returns {Array<Array<object>>}
 */
export function splitOnDividers(blocks) {
  const groups = [];
  let current  = [];
  for (const b of blocks) {
    if (b.type === "divider") {
      groups.push(current);
      current = [];
    } else {
      current.push(b);
    }
  }
  groups.push(current);
  return groups.filter(g => g.length > 0);
}

/**
 * Splits blocks at heading boundaries for page-break insertion.
 * @param {Array<object>} blocks
 * @param {"h1"|"h2"|"h1h2"|"auto"|"none"} mode
 * @returns {Array<Array<object>>}
 */
export function splitOnHeadings(blocks, mode) {
  let breakTypes;
  if (mode === "h1") {
    breakTypes = new Set(["heading_1"]);
  } else if (mode === "h2") {
    breakTypes = new Set(["heading_2"]);
  } else if (mode === "h1h2") {
    breakTypes = new Set(["heading_1", "heading_2"]);
  } else if (mode === "auto") {
    const LEVELS = ["heading_1", "heading_2", "heading_3"];
    let topLevel = -1;
    for (const b of blocks) {
      const idx = LEVELS.indexOf(b.type);
      if (idx !== -1 && (topLevel === -1 || idx < topLevel)) topLevel = idx;
      if (topLevel === 0) break;
    }
    if (topLevel === -1) return [blocks];
    breakTypes = new Set([LEVELS[topLevel]]);
  } else {
    return [blocks];
  }

  const HEADING_SET = new Set(["heading_1", "heading_2", "heading_3"]);
  const groups = [];
  let current = [];
  for (const b of blocks) {
    const prev = current.length ? current[current.length - 1] : null;
    const prevIsHeading = prev && HEADING_SET.has(prev.type);
    if (breakTypes.has(b.type) && current.length > 0 && !prevIsHeading) {
      groups.push(current);
      current = [];
    }
    current.push(b);
  }
  if (current.length) groups.push(current);
  return groups.filter(g => g.length > 0);
}

/**
 * Splits a group of blocks into typed segments (html, table, image)
 * for insertion via different Word APIs.
 * @param {Array<object>} blocks
 * @returns {Array<{type: "html", blocks: Array<object>}|{type: "table", block: object}|{type: "image", block: object}>}
 */
export function splitIntoSegments(blocks) {
  const segments  = [];
  let   htmlBlocks = [];

  for (const b of blocks) {
    if (b.type === "table") {
      if (htmlBlocks.length) { segments.push({ type: "html", blocks: htmlBlocks }); htmlBlocks = []; }
      segments.push({ type: "table", block: b });
    } else if (b.type === "image") {
      if (htmlBlocks.length) { segments.push({ type: "html", blocks: htmlBlocks }); htmlBlocks = []; }
      segments.push({ type: "image", block: b });
    } else {
      htmlBlocks.push(b);
    }
  }
  if (htmlBlocks.length) segments.push({ type: "html", blocks: htmlBlocks });
  return segments;
}
